import { DailySummaryResponse } from './types';

import type {
  ConcentrationCurveStatusResponse,
  CoverageSummaryStatusResponse,
  DailySummaryStatusResponse,
  ExportStatusResponse,
  MetricCorrelationStatusResponse,
  PopularPathsStatusResponse,
  PathOverlapStatusResponse,
  PreviewStatusResponse,
} from './api';

type PollableStatusResponse =
  | DailySummaryStatusResponse
  | PopularPathsStatusResponse
  | ExportStatusResponse
  | CoverageSummaryStatusResponse
  | ConcentrationCurveStatusResponse
  | MetricCorrelationStatusResponse
  | PathOverlapStatusResponse
  | PreviewStatusResponse;

const DEFAULT_TIMEOUT_MS = 600000; // 10 minutes
const DEFAULT_POLL_INTERVAL_MS = 5000; // 5 seconds

//  Reusable function to poll for results
export const pollForResults = async <T extends PollableStatusResponse>(
  key: string,
  fetchStatusFn: (key: string) => Promise<T>,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
): Promise<T> => {
  const startTime = Date.now();

  // Poll immediately first
  let response = await fetchStatusFn(key);

  while (response.status === 'Pending') {
    // Check timeout
    if (Date.now() - startTime > timeoutMs) {
      throw new Error(`Timeout waiting for results for key: ${key}`);
    }

    // Wait before next poll
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));

    // Poll again
    response = await fetchStatusFn(key);
  }

  if (response.status === 'Error') {
    throw new Error(
      response.error_message || `Error processing request for key: ${key}`,
    );
  }

  return response;
};

// Splitting the date range into chunks to make smaller requests to the backend
export const splitDateRangeIntoChunks = (
  startDate: string,
  endDate: string,
  chunkSizeDays: number,
): [string, string][] => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const chunks: [string, string][] = [];

  let chunkStart = start;

  while (chunkStart <= end) {
    // Calculate chunk end date (chunkSizeDays - 1 to avoid overlap)
    const chunkEnd = new Date(chunkStart);
    chunkEnd.setDate(chunkEnd.getDate() + chunkSizeDays - 1);

    // Don't go past the original end date
    const finalChunkEnd = chunkEnd > end ? end : chunkEnd;

    chunks.push([
      chunkStart.toISOString().slice(0, 10),
      finalChunkEnd.toISOString().slice(0, 10),
    ]);

    // Move to next chunk
    chunkStart = new Date(chunkStart);
    chunkStart.setDate(chunkStart.getDate() + chunkSizeDays);
  }

  return chunks;
};

// Merge the results from the chunks into a single result
export const mergeDailySummaryResults = (
  chunks: DailySummaryResponse[],
): DailySummaryResponse => {
  if (chunks.length === 0) {
    return { daily_summary: [], error_message: null };
  }

  // Group by metric_id using a Map for better readability
  const dataById = new Map<number, [number, number][]>();

  for (const { metric_id, data } of chunks.flatMap(
    (chunk) => chunk.daily_summary,
  )) {
    if (!dataById.has(metric_id)) {
      dataById.set(metric_id, []);
    }
    dataById.get(metric_id)?.push(...data);
  }

  // Build the merged result
  const mergedResults = Array.from(dataById.entries()).map(
    ([metric_id, data]) => ({
      metric_id,
      data: data.sort((a, b) => a[0] - b[0]),
    }),
  );

  return {
    daily_summary: mergedResults,
    error_message: chunks[0].error_message ?? null,
  };
};
